/**
 * Strict Phone Number Validator for Admin Panel Forms
 *
 * Rejects fake/dummy phone numbers (9999999999, 8888888888, 1234567890, etc.)
 * and ensures 10-digit Indian mobile standard format (starts with 6, 7, 8, 9).
 */

const KNOWN_FAKE_PATTERNS = new Set([
  "1234567890",
  "0123456789",
  "9876543210",
  "8765432109",
  "7654321098",
  "9898989898",
  "9797979797",
  "9696969696",
  "9595959595",
  "9123456789",
  "9012345678",
]);

/**
 * Validates a mobile number.
 * @param {string|number} phone
 * @returns {{ isValid: boolean, cleanPhone: string, error?: string, message?: string }}
 */
export function validatePhoneNumber(phone) {
  if (phone === undefined || phone === null || String(phone).trim() === "") {
    return { isValid: false, cleanPhone: "", error: "Phone number is required.", message: "Phone number is required." };
  }

  // Convert to string and strip non-digit characters
  let clean = String(phone).replace(/\D/g, "");

  // Normalize leading country code or trunk prefix (+91, 91, or 0)
  if (clean.length === 12 && clean.startsWith("91")) {
    clean = clean.slice(2);
  } else if (clean.length === 11 && clean.startsWith("0")) {
    clean = clean.slice(1);
  }

  // Must be exactly 10 digits
  if (clean.length !== 10) {
    const error = "Phone number must be exactly 10 digits.";
    return {
      isValid: false,
      cleanPhone: clean,
      error,
      message: error,
    };
  }

  // Must start with valid Indian mobile prefix (6, 7, 8, 9)
  if (!/^[6-9]/.test(clean)) {
    const error = "Please enter a valid mobile number starting with 6, 7, 8, or 9.";
    return {
      isValid: false,
      cleanPhone: clean,
      error,
      message: error,
    };
  }

  // Reject repeating identical digits (e.g. 9999999999, 8888888888, 7777777777, 0000000000)
  if (/^(\d)\1{9}$/.test(clean)) {
    const error = "Please enter a valid, authentic mobile number.";
    return {
      isValid: false,
      cleanPhone: clean,
      error,
      message: error,
    };
  }

  // Reject known sequential/repeating test numbers
  if (KNOWN_FAKE_PATTERNS.has(clean)) {
    const error = "Please enter a valid, authentic mobile number.";
    return {
      isValid: false,
      cleanPhone: clean,
      error,
      message: error,
    };
  }

  // Reject numbers with 7 or more identical digits in a row (e.g. 9999999123, 9899999999)
  if (/(\d)\1{6,}/.test(clean)) {
    const error = "Please enter a valid, authentic mobile number.";
    return {
      isValid: false,
      cleanPhone: clean,
      error,
      message: error,
    };
  }

  return { isValid: true, cleanPhone: clean };
}

export default validatePhoneNumber;
